import { create } from 'zustand';
import { supabase } from './supabase';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { log } from '../components/DebugLogger';

export type CardFinish = string;

export interface CollectionCard {
  id: string;
  cardId: string;
  setId: string;
  quantity: number;
  finish: CardFinish;
  username?: string;
  card_name?: string;
  card_number?: string;
  set_name?: string;
}

interface CollectionStore {
  collection: CollectionCard[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  isLoading: boolean;
  // Actions
  addCard: (card: Card, quantity?: number, finish?: CardFinish) => Promise<void>;
  addCompleteSet: (cards: Card[], finish?: CardFinish) => Promise<void>;
  removeCompleteSet: (setId: string, finish?: CardFinish) => Promise<void>;
  removeCard: (cardId: string, finish: CardFinish, removeAll?: boolean) => Promise<void>;
  updateCard: (cardId: string, updates: Partial<CollectionCard>) => Promise<void>;
  loadCollection: () => Promise<void>;
  getCardQuantity: (cardId: string, finish: CardFinish) => number;
  getCardDetails: (cardId: string, finish: CardFinish) => CollectionCard | undefined;
  reset: () => void;
}

export const useCollectionStore = create<CollectionStore>((set, get) => ({
  collection: [],
  loading: false,
  error: null,
  initialized: false,
  isLoading: false,

  reset: () => {
    set({
      collection: [],
      loading: false,
      error: null,
      initialized: false,
      isLoading: false
    });
  },

  addCard: async (card, quantity = 1, finish = 'normal') => {
    try {
      set({ loading: true, error: null });
      log(`Starting addCard operation for ${card.name}...`);
      const currentState = get();

      if (!card || !card.id || !card.set) {
        log('Invalid card data provided to addCard');
        console.error('Invalid card data:', card);
        throw new Error('Invalid card data: missing required fields');
      }

      const setId = card.set.id;
      if (!setId) {
        console.error('Invalid card data:', card);
        throw new Error('Invalid card data: missing set identifier');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session found');
        throw new Error('You must be signed in to add cards to your collection');
      }
      
      log(`Checking for existing card in collection: ${card.name}`);
      const { data: existingCards, error: fetchError } = await supabase
        .from('collections')
        .select()
        .eq('card_id', card.id)
        .eq('user_id', session.user.id)
        .eq('card_finish', finish);

      if (fetchError) throw fetchError;

      const existingCard = existingCards?.[0];

      if (existingCard) {
        log(`Updating existing card quantity from ${existingCard.quantity} to ${existingCard.quantity + quantity}`);
        const newQuantity = existingCard.quantity + quantity;
        const { error } = await supabase
          .from('collections')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('card_id', card.id)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set({
          collection: currentState.collection.map(c => 
            c.cardId === card.id && c.finish === finish
              ? { ...c, quantity: newQuantity }
              : c
          )
        });
        log(`Local state updated for ${card.name}`);

        // Trigger collection reload
        await get().loadCollection();
      } else {
        const newCard = {
          id: crypto.randomUUID(),
          cardId: card.id,
          setId: setId,
          quantity,
          finish
        };

        log('Adding new card to collection');
        const { error } = await supabase
          .from('collections')
          .insert({
            card_id: newCard.cardId,
            user_id: session.user.id,
            set_id: newCard.setId,
            quantity,
            card_finish: finish,
            card_name: card.name,
            card_number: card.number,
            set_name: card.set.name
          });

        if (error) {
          console.error('Error adding card:', error);
          log('Error adding card to collection:', error.message);
          throw new Error(error.message);
        }

        log('Successfully added new card to collection');
        
        // Update local state immediately
        set({
          collection: [...currentState.collection, newCard]
        });
        log(`Local state updated with new card ${card.name}`);

        // Trigger collection reload
        await get().loadCollection();
      }
    } catch (error) {
      log('Error in addCard operation:', error instanceof Error ? error.message : 'Unknown error');
      console.error('Error in addCard:', error);
      set({ error: error instanceof Error ? error.message : 'Error adding card' });
      throw error;
    } finally {
      set({ loading: false });
      log('Completed addCard operation');
    }
  },

  addCompleteSet: async (cards, finish = 'normal') => {
    try {
      set({ loading: true, error: null });
      log(`Starting addCompleteSet operation for ${cards.length} cards...`);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session found');
        throw new Error('You must be signed in to add cards to your collection');
      }

      if (!cards || cards.length === 0) {
        throw new Error('No cards provided to add to collection');
      }

      // Validate all cards first
      const validCards = cards.filter(card => card && card.id && card.set && card.set.id);
      if (validCards.length !== cards.length) {
        log(`Warning: ${cards.length - validCards.length} invalid cards filtered out`);
      }

      if (validCards.length === 0) {
        throw new Error('No valid cards to add to collection');
      }

      // Get existing cards in collection for this user and finish
      const cardIds = validCards.map(card => card.id);
      const { data: existingCards, error: fetchError } = await supabase
        .from('collections')
        .select()
        .eq('user_id', session.user.id)
        .eq('card_finish', finish)
        .in('card_id', cardIds);

      if (fetchError) throw fetchError;

      const existingCardMap = new Map(
        existingCards?.map(card => [card.card_id, card]) || []
      );

      // Prepare batch operations
      const cardsToInsert: any[] = [];
      const cardsToUpdate: any[] = [];

      validCards.forEach(card => {
        const existingCard = existingCardMap.get(card.id);
        
        if (existingCard) {
          // Update existing card
          cardsToUpdate.push({
            id: existingCard.id,
            quantity: existingCard.quantity + 1,
            updated_at: new Date().toISOString()
          });
        } else {
          // Insert new card
          cardsToInsert.push({
            card_id: card.id,
            user_id: session.user.id,
            set_id: card.set.id,
            quantity: 1,
            card_finish: finish,
            card_name: card.name,
            card_number: card.number,
            set_name: card.set.name
          });
        }
      });

      // Execute batch operations
      if (cardsToInsert.length > 0) {
        log(`Inserting ${cardsToInsert.length} new cards`);
        const { error: insertError } = await supabase
          .from('collections')
          .insert(cardsToInsert);
        
        if (insertError) {
          console.error('Error inserting cards:', insertError);
          throw new Error(insertError.message);
        }
      }

      if (cardsToUpdate.length > 0) {
        log(`Updating ${cardsToUpdate.length} existing cards`);
        for (const cardUpdate of cardsToUpdate) {
          const { error: updateError } = await supabase
            .from('collections')
            .update({
              quantity: cardUpdate.quantity,
              updated_at: cardUpdate.updated_at
            })
            .eq('id', cardUpdate.id);
          
          if (updateError) {
            console.error('Error updating card:', updateError);
            throw new Error(updateError.message);
          }
        }
      }

      log(`Successfully added complete set: ${validCards.length} cards`);
      
      // Reload collection to get updated state
      await get().loadCollection();
    } catch (error) {
      log('Error in addCompleteSet operation:', error instanceof Error ? error.message : 'Unknown error');
      console.error('Error in addCompleteSet:', error);
      set({ error: error instanceof Error ? error.message : 'Error adding complete set' });
      throw error;
    } finally {
      set({ loading: false });
      log('Completed addCompleteSet operation');
    }
  },

  removeCompleteSet: async (setId, finish) => {
    try {
      set({ loading: true, error: null });
      log(`Starting removeCompleteSet operation for set ${setId}...`);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session found');
        throw new Error('You must be signed in to remove cards from your collection');
      }

      if (!setId) {
        throw new Error('No set ID provided');
      }

      // Build query to remove cards from this set
      let deleteQuery = supabase
        .from('collections')
        .delete()
        .eq('user_id', session.user.id)
        .eq('set_id', setId);

      // If finish is specified, only remove cards with that finish
      if (finish) {
        deleteQuery = deleteQuery.eq('card_finish', finish);
      }

      const { error: deleteError } = await deleteQuery;

      if (deleteError) {
        console.error('Error removing complete set:', deleteError);
        throw new Error(deleteError.message);
      }

      log(`Successfully removed complete set: ${setId}${finish ? ` (${finish})` : ''}`);
      
      // Reload collection to get updated state
      await get().loadCollection();
    } catch (error) {
      log('Error in removeCompleteSet operation:', error instanceof Error ? error.message : 'Unknown error');
      console.error('Error in removeCompleteSet:', error);
      set({ error: error instanceof Error ? error.message : 'Error removing complete set' });
      throw error;
    } finally {
      set({ loading: false });
      log('Completed removeCompleteSet operation');
    }
  },

  removeCard: async (cardId, finish, removeAll = false) => {
    try {
      set({ loading: true, error: null });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be signed in to remove cards from your collection');
      }

      const existingCard = get().collection.find(c => c.cardId === cardId && c.finish === finish);
      if (!existingCard) {
        throw new Error('Card not found in collection');
      }

      if (removeAll || existingCard.quantity <= 1) {
        // Remove the entire card entry
        const { error } = await supabase
          .from('collections')
          .delete()
          .eq('card_id', cardId)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set(state => ({
          collection: state.collection.filter(card => 
            !(card.cardId === cardId && card.finish === finish)
          )
        }));

        // Trigger collection reload
        await get().loadCollection();
      } else {
        // Decrease quantity by 1
        const newQuantity = existingCard.quantity - 1;
        const { error } = await supabase
          .from('collections')
          .update({ 
            quantity: newQuantity,
            updated_at: new Date().toISOString()
          })
          .eq('card_id', cardId)
          .eq('user_id', session.user.id)
          .eq('card_finish', finish);

        if (error) throw error;

        // Update local state immediately
        set(state => ({
          collection: state.collection.map(card => 
            card.cardId === cardId && card.finish === finish
              ? { ...card, quantity: newQuantity }
              : card
          )
        }));

        // Trigger collection reload
        await get().loadCollection();
      }
    } catch (error) {
      console.error('Error removing card:', error);
      set({ error: error instanceof Error ? error.message : 'Error removing card' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateCard: async (cardId, updates) => {
    try {
      set({ loading: true, error: null });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be signed in to update your collection');
      }

      if (updates.quantity !== undefined && updates.quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
      }
      
      const { error } = await supabase
        .from('collections')
        .update({
          ...updates,
          card_finish: updates.finish,
          updated_at: new Date().toISOString()
        })
        .eq('card_id', cardId)
        .eq('user_id', session.user.id)
        .eq('card_finish', updates.finish);

      if (error) throw error;

      // Update local state immediately
      set(state => ({
        collection: state.collection.map(card => 
          card.cardId === cardId && card.finish === updates.finish
            ? { ...card, ...updates }
            : card
        )
      }));

      // Trigger collection reload
      await get().loadCollection();
    } catch (error) {
      console.error('Error updating card:', error);
      set({ error: error instanceof Error ? error.message : 'Error updating card' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  loadCollection: async () => {
    try {
      if (get().isLoading) {
        return;
      }

      set({ loading: true, error: null, isLoading: true });
      log('Starting collection load');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        log('No active session, resetting collection');
        set({ collection: [], initialized: true, loading: false, isLoading: false });
        return;
      }
      
      log('Fetching collection from Supabase');
      
      // Try to fetch all records and get the exact count
      const { data: initialData, error, count } = await supabase
        .from('collections')
        .select('*', { count: 'exact' })
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
        
              let allData = initialData || [];
        
        // If we got less than the count, there's a limit issue - fetch remaining records
        if (initialData && count && initialData.length < count) {
          console.log(`📦 Fetching remaining collection records (${initialData.length}/${count})...`);
          
          let offset = initialData.length;
          
          while (offset < count) {
            const { data: moreData, error: moreError } = await supabase
              .from('collections')
              .select('*')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false })
              .range(offset, offset + 999);
              
            if (moreError) {
              console.error('Error fetching more data:', moreError);
              break;
            }
            
            if (!moreData || moreData.length === 0) {
              break;
            }
            
            allData = [...allData, ...moreData];
            offset += moreData.length;
          }
          
          console.log(`📦 Collection batch fetch complete: ${allData.length} total records`);
        }
      
      const data = allData;

      if (error) {
        log(`Error fetching collection: ${error.message}`);
        throw error;
      }

      if (!data) {
        log('No collection data returned');
        set({ collection: [], initialized: true, loading: false, isLoading: false });
        return;
      }

      log(`Loaded ${data.length} cards from collection`);
      
      const mappedCollection = data.map(item => ({
        id: item.id,
        cardId: item.card_id,
        setId: item.set_id,
        quantity: item.quantity,
        finish: item.card_finish || 'normal',
        username: item.username,
        card_name: item.card_name,
        card_number: item.card_number,
        set_name: item.set_name
      }));
      
      const totalCards = mappedCollection.reduce((sum, card) => sum + card.quantity, 0);
      console.log(`✅ Collection loaded: ${mappedCollection.length} unique cards, ${totalCards} total cards`);
      
      set({ 
        collection: mappedCollection,
        initialized: true
      });
      log('Collection state updated successfully');
    } catch (error) {
      log(`Error loading collection: ${error instanceof Error ? error.message : 'Unknown error'}`);
      set({ error: error instanceof Error ? error.message : 'Error loading collection' });
    } finally {
      set({ loading: false, isLoading: false });
      log('Collection load complete');
    }
  },

  getCardQuantity: (cardId, finish) => {
    const card = get().collection.find(c => c.cardId === cardId && c.finish === finish);
    return card?.quantity || 0;
  },

  getCardDetails: (cardId, finish) => {
    return get().collection.find(c => c.cardId === cardId && c.finish === finish);
  }
}));